import React, { useRef, useState, useEffect } from 'react';
import { APP_COLORS as C, APP_FONT as F, DASH_SIZES } from '../tokens';
import { SERVICES } from '../constants';
import type { ServicePill, ServiceId } from '../types';

type Props = {
  onServiceClick?: (service: ServicePill) => void;
};

type ScrollState = 'fits' | 'start' | 'middle' | 'end';

const CIRCLE_BG = '#1E293B';

const SVC_ICON_COLORS: Record<ServiceId, string> = {
  expenses: '#0F6E56',
  accounting: '#15A08C',
  sales: '#F43F5E',
  customer: '#3B82F6',
  payroll: '#8B5CF6',
  team: '#F97316',
  tax: '#EC4899',
  marketing: '#FBBF24',
  hr: '#10B981',
};

function ServiceIcon({ id }: { id: ServiceId }): React.ReactElement | null {
  const color = SVC_ICON_COLORS[id] || '#fff';
  switch (id) {
    case 'expenses':
      return (<svg viewBox="0 0 24 24" width={19} height={19}><path fill={color} d="M6 2h12v20l-3-2-3 2-3-2-3 2z"/><rect x="8" y="6" width="8" height="1.6" fill={CIRCLE_BG}/><rect x="8" y="10" width="8" height="1.6" fill={CIRCLE_BG}/><rect x="8" y="14" width="5" height="1.6" fill={CIRCLE_BG}/></svg>);
    case 'accounting':
      return (<svg viewBox="0 0 24 24" width={19} height={19}><path fill={color} d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z"/></svg>);
    case 'sales':
      return (<svg viewBox="0 0 24 24" width={19} height={19}><rect x="4" y="19.5" width="16" height="1.8" fill={color}/><rect x="6" y="13" width="3" height="7" fill={color}/><rect x="11" y="9" width="3" height="11" fill={color}/><rect x="16" y="5" width="3" height="15" fill={color}/></svg>);
    case 'customer':
      return (<svg viewBox="0 0 24 24" width={19} height={19}><circle cx="12" cy="9" r="3.5" fill={color}/><path fill={color} d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6v1H5z"/><path fill={color} d="M17 3h3a1 1 0 011 1v3a1 1 0 01-1 1h-2l-2 2V3z"/></svg>);
    case 'payroll':
      return (<svg viewBox="0 0 24 24" width={19} height={19}><circle cx="12" cy="12" r="9" fill={color}/><circle cx="12" cy="12" r="5.5" fill={CIRCLE_BG}/><circle cx="12" cy="12" r="2.5" fill={color}/></svg>);
    case 'team':
      return (<svg viewBox="0 0 24 24" width={19} height={19}><circle cx="8" cy="8" r="3" fill={color}/><circle cx="16" cy="8" r="3" fill={color}/><path fill={color} d="M2 20c0-3 2.5-5 6-5s6 2 6 5v1H2z"/><path fill={color} d="M14 20c0-3 2-5 4-5s4 2 4 5v1h-8z"/></svg>);
    case 'tax':
      return (<svg viewBox="0 0 24 24" width={19} height={19}><circle cx="12" cy="12" r="9" fill={color}/><line x1="8" y1="16" x2="16" y2="8" stroke={CIRCLE_BG} strokeWidth={2} strokeLinecap="round"/><circle cx="9" cy="9" r="1.5" fill={CIRCLE_BG}/><circle cx="15" cy="15" r="1.5" fill={CIRCLE_BG}/></svg>);
    case 'marketing':
      return (<svg viewBox="0 0 24 24" width={19} height={19}><path fill={color} d="M12 2l2.5 7h7l-5.7 4.3 2.2 6.7-6-4.4-6 4.4 2.2-6.7-5.7-4.3h7z"/></svg>);
    case 'hr':
      return (<svg viewBox="0 0 24 24" width={19} height={19}><circle cx="10" cy="8" r="4" fill={color}/><path fill={color} d="M3 21c0-4 3-7 7-7s7 3 7 7v1H3z"/><circle cx="18" cy="17" r="4" fill={color}/><polyline points="16 17 17.5 18.5 20 16" stroke={CIRCLE_BG} strokeWidth={2.5} fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>);
    default:
      return null;
  }
}

export default function ServicePills({ onServiceClick }: Props) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hovered, setHovered] = useState<ServiceId | null>(null);
  const [scrollState, setScrollState] = useState<ScrollState>('fits');

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      if (scrollWidth <= clientWidth + 2) {
        setScrollState('fits');
      } else if (scrollLeft < 10) {
        setScrollState('start');
      } else if (scrollLeft >= scrollWidth - clientWidth - 10) {
        setScrollState('end');
      } else {
        setScrollState('middle');
      }
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const handleScroll = (direction: 'left' | 'right') => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const el = scrollRef.current;
    if (!el) return;
    const delta = direction === 'right' ? 320 : -320;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const target = Math.max(0, Math.min(maxScroll, el.scrollLeft + delta));
    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  const showRight = scrollState !== 'end';
  const showLeft = scrollState === 'middle' || scrollState === 'end';

  return (
    <div style={{
      position: 'sticky',
      top: -28,
      zIndex: 10,
      background: C.card,
      borderBottom: '1px solid ' + C.line,
      marginLeft: -32,
      marginRight: -32,
      marginBottom: 20,
      padding: '10px 32px',
      fontFamily: F.family,
    }}>
      <div style={{ position: 'relative' }}>
        <div
          ref={scrollRef}
          style={{
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            padding: '8px 8px',
            scrollbarWidth: 'none',
          }}
        >
          {SERVICES.map((svc) => {
            const isHover = hovered === svc.id;
            return (
              <div
                key={svc.id}
                onMouseEnter={() => setHovered(svc.id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onServiceClick?.(svc)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 11,
                  padding: '7px 22px 7px 7px',
                  background: isHover ? C.pillHover : C.card,
                  border: '1px solid ' + C.line,
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: C.ink,
                  flexShrink: 0,
                  transition: 'background 0.15s ease',
                  height: DASH_SIZES.servicePillHeight,
                }}
              >
                <div style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: CIRCLE_BG,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}>
                  <ServiceIcon id={svc.id} />
                </div>
                {svc.label}
              </div>
            );
          })}
          <div style={{ flex: '0 0 320px', minWidth: 320 }} aria-hidden />
        </div>

        {showRight && (
          <div
            onClick={handleScroll('right')}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: 64,
              background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, #fff 40%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: 10,
              cursor: 'pointer',
              zIndex: 5,
              pointerEvents: 'auto',
            }}
          >
            <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke={C.ink} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        )}

        {showLeft && (
          <div
            onClick={handleScroll('left')}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 64,
              background: 'linear-gradient(270deg, rgba(255,255,255,0) 0%, #fff 40%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              paddingLeft: 10,
              cursor: 'pointer',
              zIndex: 5,
              pointerEvents: 'auto',
            }}
          >
            <svg viewBox="0 0 24 24" width={20} height={20} fill="none" stroke={C.ink} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </div>
        )}
      </div>
    </div>
  );
}
